import { ContentType } from "./dataTypes/ContentType";
import { FieldDescriptor, FieldSelectOption } from "./FieldDescriptor";
import { Field, MediaField } from "./graphFieldsExtractor";

/**
 * Advanced (presentation / runtime) field catalog — roadmap Phase 4 (§2.A/§2.B/§2.C).
 *
 * The node-form's Core fields come from two paths (the backend unified schema
 * for slopify types, the frontend legacy extractor for the rest). Advanced
 * presentation columns (font_size, color, timers, volume, looping, region
 * sizes, …) live in NEITHER source — they're non-translatable content columns
 * that the slopify schema and the PLD map don't carry. Rather than teach both
 * paths about them, we inject them from this single frontend catalog AFTER the
 * Core fields are extracted (`buildAdvancedFields` in NodeForm), so one table
 * covers every type regardless of which Core path produced its fields.
 *
 * Scalar advanced fields carry `tier: "advanced"` and render inside the
 * collapsible "Advanced" section. Advanced MEDIA fields (audio_success/fail,
 * source_video_tuto, …) render in the media strip alongside Core media — their
 * tier is documentation/ordering only (§0.4), so they're plain MediaFields.
 *
 * Numeric bounds / display modes come verbatim from Appendix C (the VR editor
 * prefab slider ranges). `displayMode: "percent"` is DISPLAY-ONLY — the raw
 * float is stored (Appendix C load-bearing note); DynamicField mirrors the %.
 */

/** Base content columns whose VR write-target is the base `contents` row
 * (§0.2 / §2.A / §2.D pt 9). On save these route to `PUT /contents/:id`, never
 * to the per-type endpoint (graphLegacy.updateContentType). */
export const BASE_CONTENT_FIELD_KEYS = ["time_code", "is_pause_scene_on_play", "be_scored"] as const;

type NumberSpec = {
  kind: "number";
  key: string;
  min?: number;
  max?: number;
  displayMode?: "decimal" | "integer" | "percent";
  // Render in the Core grid instead of the collapsible Advanced section (used
  // by the avatar track, whose behavior switches are primary configuration).
  core?: boolean;
  // i18n key for a per-field help tooltip (info icon next to the label). Scoped
  // to the content type where it's declared — e.g. `timer` means "0 = infinite"
  // on text/onboarding but a plain countdown on the zone games, so the wording
  // rides on the spec, not the shared field name. Overrides the generic
  // secondsHint tooltip in DynamicField.
  helpKey?: string;
};
type BooleanSpec = { kind: "boolean"; key: string; labelKey?: string; core?: boolean };
type ColorSpec = { kind: "color"; key: string };
type MediaSpec = { kind: "media"; key: string; mediaType: MediaField["mediaType"] };
type SelectSpec = { kind: "select"; key: string; options: FieldSelectOption[]; core?: boolean };
// A JSON list column edited by a bespoke Core-grid editor (NodeForm renders a
// dedicated component per modelName+key, like element_list / hotspot_info).
type ArraySpec = { kind: "arrayOfObjects"; key: string };
type AdvancedSpec = NumberSpec | BooleanSpec | ColorSpec | MediaSpec | SelectSpec | ArraySpec;

const num = (
  key: string,
  min: number | undefined,
  max: number | undefined,
  displayMode: NumberSpec["displayMode"],
  core?: boolean,
): NumberSpec => ({ kind: "number", key, min, max, displayMode, core });
const bool = (key: string, labelKey?: string, core?: boolean): BooleanSpec => ({
  kind: "boolean",
  key,
  labelKey,
  core,
});
const color = (key: string): ColorSpec => ({ kind: "color", key });
const media = (key: string, mediaType: MediaField["mediaType"]): MediaSpec => ({
  kind: "media",
  key,
  mediaType,
});
const sel = (key: string, options: FieldSelectOption[], core?: boolean): SelectSpec => ({
  kind: "select",
  key,
  options,
  core,
});
const arr = (key: string): ArraySpec => ({ kind: "arrayOfObjects", key });

// Base runtime toggles VR exposes on every prefab (§2.D pt 9). `time_code` has
// no VR slider bounds (its own SetTimeCode widget) → seconds, min 0. Applied to
// every INTERACTION type below — but NOT to scenes (Sébastien 2026-07-16: a
// scene has no timeline slot of its own, both toggles only make sense on the
// children of a video scene; NodeForm additionally disables them when the
// parent scene isn't a video360). The avatar track is handled separately (§4).
const BASE_UNIVERSAL: AdvancedSpec[] = [
  num("time_code", 0, undefined, "decimal"),
  bool("is_pause_scene_on_play"),
];

/** Per-type advanced specs (excluding the universal base toggles above). */
const PER_TYPE: Record<string, AdvancedSpec[]> = {
  "content-text": [
    num("font_size", 10, 60, "decimal"),
    color("color"),
    bool("has_background"),
    // `does_disable_self_from_timer` removed (2026-07-17): Unity ignores it —
    // deactivation is gated purely on `timer` (0 = infinite). See timerInfiniteHint.
    { ...num("timer", 0, 50, "decimal"), helpKey: "pages.project.editNode.timerInfiniteHint" },
  ],
  "content-onboarding": [
    num("font_size", 10, 100, "decimal"),
    { ...num("timer", 0, 50, "decimal"), helpKey: "pages.project.editNode.timerInfiniteHint" },
  ],
  "content-danger-catch": [
    num("font_size", 10, 80, "decimal"),
    num("catch_time", 1, 10, "integer"),
    bool("is_hidden_at_start"),
    bool("be_scored"),
    media("audio_success", "audio"),
    media("audio_fail", "audio"),
  ],
  "content-photo-catch": [
    num("font_size", 10, 80, "decimal"),
    num("catch_time", 1, 10, "integer"),
    bool("is_hidden_at_start"),
    bool("be_scored"),
  ],
  "content-thumbnail": [num("font_size", 10, 75, "decimal"), color("color")],
  "content-hotspot-scene-image": [
    color("color"),
    num("font_size", 10, 80, "decimal"),
    bool("has_activate_animation"),
    bool("is_hidden_at_start"),
  ],
  "content-hotspot-scene-video": [
    color("color"),
    num("font_size", 10, 80, "decimal"),
    bool("has_activate_animation"),
    bool("is_hidden_at_start"),
  ],
  "content-video-2-d": [
    color("color"),
    // VR labels the video loop toggle differently from the audio/scene loop
    // (loc_videoLoop "Video loop" vs loc_loop "Is looping") — per-type label.
    bool("is_looping", "is_looping_video"),
    bool("has_video_controller"),
    num("sound_volume", 0, 1, "percent"),
  ],
  "content-audio": [
    bool("is_looping"),
    bool("is_spatialized"),
    num("sound_distance", 0, 1, "decimal"),
    num("sound_volume", 0, 1, "percent"),
  ],
  "content-avatar-audio-player": [
    bool("is_looping"),
    bool("is_spatialized"),
    num("sound_distance", 0, 1, "decimal"),
    num("sound_volume", 0, 1, "percent"),
  ],
  "content-3-d-viewer": [num("height", 0, 1, "decimal"), bool("is_self_rotating")],
  "content-3d-viewer": [num("height", 0, 1, "decimal"), bool("is_self_rotating")],
  "content-pdf-viewer": [num("page_to_start", 1, undefined, "integer")],
  "content-fire-extinguisher": [bool("be_scored"), media("source_video_tuto", "video")],
  "content-zone-sort": [
    num("timer", 0, 60, "decimal"),
    bool("be_scored"),
    media("source_video_tuto", "video"),
  ],
  "content-zone-detect": [num("timer", 0, 60, "decimal"), bool("be_scored")],
  "content-clean": [media("source_video_tuto", "video")],
  "content-element-catch": [
    num("game_duration", 1, 60, "integer"),
    bool("be_scored"),
    media("source_video_tuto", "video"),
  ],
  "content-random-quizz": [num("nb_questions", 1, 20, "integer"), bool("be_scored")],
  "content-speech-to-check": [
    num("respond_timer", 1, 30, "decimal"),
    num("time_begin", 0, 30, "integer"),
    bool("be_scored"),
  ],
  // Scorable quizz family + vr-locator (Sébastien 2026-07-16: be_scored was
  // missing from nearly every scorable interaction). Set = the types with
  // be_scored=true rows in live data; the toggle routes to the base contents
  // row like every BASE_CONTENT_FIELD_KEYS column.
  "content-quizz-1": [bool("be_scored")],
  "content-quizz-2": [bool("be_scored")],
  "content-quizz-3": [bool("be_scored")],
  "content-yes-no-question": [bool("be_scored")],
  "content-quizz-sort": [bool("be_scored")],
  "content-quizz-image-multiple": [bool("be_scored")],
  "content-vr-locator": [bool("be_scored")],
  "content-blur": [
    num("blur_region_width", 0.01, 7, "decimal"),
    num("blur_region_height", 0.01, 7, "decimal"),
  ],
  "content-fire": [num("fire_size", 0.01, 7, "percent")],
  "content-dust": [color("color")],
  "content-highlight": [num("content_size", 0.5, 2, "decimal")],
  "content-tuto": [bool("has_audio")],
  "content-button-1": [color("color")],
  "content-button-reload": [color("color")],
  // scene sound_volume/is_looping are conditional (§2.D pt 3) — enabled only for
  // video360 scenes. They live here so they render in the Advanced section; the
  // conditional disable + tooltip is applied in NodeForm.
  "content-scene": [bool("is_looping"), num("sound_volume", 0, 1, "percent")],
  // ── Avatar track (scoped 2026-07-16 against the AI-service contract, not VR
  // parity — see §4 of the parity plan). Deliberate exclusions:
  //  - voice_id      → VESTIGIAL column; the playing voice comes from the avatar
  //                    source-media's metadata (tts-voice-data-model.md)
  //  - avatar_url    → legacy, doubled by source_avatar
  //  - language      → the backend resolves it from the project language
  //                    (resolveAvatarLanguage); exposing it invites conflicts
  //  - debrief_chat_service → dead column, zero consumers (Unity + backend)
  "content-avatar-ai-persona": [
    // Core behavior switches (VR persona editor parity: chat toggles +
    // max-dialog input, VRCAvatarAIPersonaBodyController).
    sel(
      "chat_service",
      [
        { value: "openai", optionText: "ChatGPT" },
        { value: "mistral", optionText: "Mistral" },
      ],
      true,
    ),
    num("max_nb_dialog", 1, 999, "integer", true), // VR clamp 1..999 (AddToMaxNbField)
    bool("activate_debriefing", undefined, true),
    // pdf_items [{ source_media_id, description, is_visible_to_avatar }] —
    // bespoke PdfItemsField editor in NodeForm.
    arr("pdf_items"),
    // Advanced: display / dialog-flow toggles + timings consumed via the
    // chat-init contract (avatar-ai.js). No VR sliders exist for the numbers —
    // bounds are web-added safety around the live-data ranges.
    bool("is_showing_user_text"),
    bool("is_showing_avatar_text"),
    bool("activate_auto_rec"),
    bool("is_dialog_started_by_user"),
    bool("is_silence_detection_activated"),
    num("min_silent_time_sec_before_avatar_reaction", 0, 30, "decimal"),
    num("max_silent_time_sec_before_avatar_reaction", 0, 30, "decimal"),
    bool("is_speech_interruption_activated"),
    bool("is_speech_interruption_by_user_activated"),
    num("avatar_height", 0, 2, "decimal"),
    num("avatar_debrief_height", 0, 2, "decimal"),
    bool("be_scored"), // scorable in live data (1351 rows); routes to base content
    media("source_avatar", "avatar"),
    media("source_avatar_debrief", "avatar"),
  ],
  "content-avatar-ai": [
    sel(
      "chat_service",
      [
        { value: "openai", optionText: "ChatGPT" },
        { value: "mistral", optionText: "Mistral" },
      ],
      true,
    ),
    num("max_nb_dialog", 1, 999, "integer", true),
    bool("allow_internet_search", undefined, true),
    // source_medias (many-relation, PDF context documents in VR) — bespoke
    // PdfItemsField editor (media-list variant) in NodeForm.
    arr("source_medias"),
    bool("is_showing_user_text"),
    bool("is_showing_avatar_text"),
    bool("activate_auto_rec"),
    media("source_avatar", "avatar"),
  ],
  // WON'T DO (kept as documentation of the deliberate omissions):
  //  - content-scoring-general.enable_redo (§2.D pt 1)
  //  - content-scene.time_begin / time_end (§2.D pt 2)
  //  - content-vr-locator.activate_start_panel — ON HOLD (Appendix A pt 1)
  //  - depth / scale / positions everywhere (§2.D pt 10)
};

// Avatar types still excluded from the catalog: only the session-debrief
// remains (persona + avatar-ai got their AI-service-scoped entries above,
// 2026-07-16; the debrief type is a follow-up).
const AVATAR_TRACK = new Set(["content-avatar-ai-session-debrief"]);

const makeDescriptor = (spec: AdvancedSpec): FieldDescriptor => {
  // `core: true` specs render in the main Core grid (tier undefined); everything
  // else lands in the collapsible Advanced section.
  const isCore = "core" in spec && spec.core === true;
  const base = {
    key: spec.key,
    description: null,
    required: false,
    pldKey: null,
    isTranslatable: false,
    tier: isCore ? undefined : ("advanced" as const),
  };
  switch (spec.kind) {
    case "number":
      return {
        ...base,
        widget: "number",
        jsonSchema: {
          type: spec.displayMode === "integer" ? "integer" : "number",
          ...(spec.min !== undefined ? { minimum: spec.min } : {}),
          ...(spec.max !== undefined ? { maximum: spec.max } : {}),
        },
        displayMode: spec.displayMode ?? "decimal",
        ...(spec.helpKey ? { helpKey: spec.helpKey } : {}),
      };
    case "boolean":
      return {
        ...base,
        widget: "boolean",
        jsonSchema: { type: "boolean" },
        ...(spec.labelKey ? { labelKey: spec.labelKey } : {}),
      };
    case "color":
      return { ...base, widget: "color", jsonSchema: { type: "string" } };
    case "select":
      return {
        ...base,
        widget: "select",
        jsonSchema: { type: "string" },
        options: spec.options,
      };
    case "arrayOfObjects":
      return { ...base, widget: "arrayOfObjects", jsonSchema: { type: "array" }, tier: undefined };
    case "media":
      return { ...base, widget: "media", jsonSchema: {}, tier: undefined };
  }
};

const BASE_CONTENT_FIELD_KEY_SET = new Set<string>(BASE_CONTENT_FIELD_KEYS);

const specToField = (
  spec: AdvancedSpec,
  contentObject: Record<string, unknown>,
  baseContentObject?: Record<string, unknown>,
): Field => {
  const descriptor = makeDescriptor(spec);
  // For columns that are authoritative on the base contents row, prefer that
  // value over the potentially-stale per-type copy.
  const currentValue =
    BASE_CONTENT_FIELD_KEY_SET.has(spec.key) && baseContentObject != null
      ? baseContentObject[spec.key] ?? contentObject?.[spec.key]
      : contentObject?.[spec.key];
  switch (spec.kind) {
    case "media":
      return {
        type: "media",
        mediaType: spec.mediaType,
        fieldName: spec.key,
        currentValue: currentValue as MediaField["currentValue"],
        descriptor,
      };
    case "boolean":
      return { type: "boolean", fieldName: spec.key, currentValue: !!currentValue, descriptor };
    case "number":
      return {
        type: "number",
        fieldName: spec.key,
        currentValue: currentValue as number | string | undefined,
        descriptor,
      };
    case "color":
      return {
        type: "color",
        fieldName: spec.key,
        currentValue: currentValue as string | undefined,
        descriptor,
      };
    case "select":
      return {
        type: "select",
        fieldName: spec.key,
        currentValue:
          currentValue === null || currentValue === undefined ? "" : String(currentValue),
        descriptor,
      };
    case "arrayOfObjects": {
      // JSON columns can arrive as strings; many-relations (source_medias) as
      // arrays of populated objects — normalize the latter to plain ids so the
      // editor and the PUT payload agree on number[].
      let arrValue: unknown[] = [];
      if (Array.isArray(currentValue)) arrValue = currentValue;
      else if (typeof currentValue === "string") {
        try {
          const parsed = JSON.parse(currentValue);
          if (Array.isArray(parsed)) arrValue = parsed;
        } catch {
          arrValue = [];
        }
      }
      if (spec.key === "source_medias") {
        arrValue = arrValue.map((e) =>
          e && typeof e === "object" ? Number((e as { id?: unknown }).id) : Number(e),
        );
      }
      return {
        type: "arrayOfObjects",
        fieldName: spec.key,
        currentValue: arrValue,
        descriptor,
      } as Field;
    }
  }
};

/**
 * Build the advanced Field[] for a content node, reading current values off the
 * content object. Excludes the avatar track. Callers dedup against the Core
 * fields already extracted (a spec whose key a Core field already covers is
 * dropped so we never render a column twice).
 */
export const buildAdvancedFields = (
  contentType: ContentType,
  baseContentObject?: Record<string, unknown>,
): Field[] => {
  const modelName = contentType.modelName;
  if (AVATAR_TRACK.has(modelName)) return [];
  // Scenes never get the base toggles (see BASE_UNIVERSAL note).
  const specs =
    modelName === "content-scene"
      ? PER_TYPE[modelName] ?? []
      : [...BASE_UNIVERSAL, ...(PER_TYPE[modelName] ?? [])];
  const contentObject = (contentType.object as unknown as Record<string, unknown>) ?? {};
  // Guard against accidental duplicate keys across the universal + per-type sets.
  const seen = new Set<string>();
  const fields: Field[] = [];
  for (const spec of specs) {
    if (seen.has(spec.key)) continue;
    seen.add(spec.key);
    fields.push(specToField(spec, contentObject, baseContentObject));
  }
  return fields;
};

export const isAdvancedField = (field: Field): boolean => field.descriptor?.tier === "advanced";

/**
 * Consistent internal ordering of the Advanced section (Sébastien 2026-07-16):
 * every advanced field belongs to one of two groups, rendered presentation
 * first, then configuration, separated by an unlabeled hairline. The split is
 * SEMANTIC, not per-type — a key always lands in the same group everywhere:
 *   - "presentation" — what the learner sees/hears: sizes, colors, animation,
 *     media playback (loop/volume/spatialization), display toggles.
 *   - "config" — how the interaction behaves: triggers/timing (time_code,
 *     timers), scoring, gameplay rules, dialog-flow switches.
 * Debatable calls, settled once here: is_hidden_at_start + timer govern WHEN
 * the content appears/disappears → config; is_looping/volume govern HOW the
 * media renders → presentation.
 */
export const ADVANCED_FIELD_GROUPS: Record<string, "presentation" | "config"> = {
  // presentation
  font_size: "presentation",
  color: "presentation",
  has_background: "presentation",
  has_activate_animation: "presentation",
  is_looping: "presentation",
  has_video_controller: "presentation",
  sound_volume: "presentation",
  sound_distance: "presentation",
  is_spatialized: "presentation",
  height: "presentation",
  is_self_rotating: "presentation",
  content_size: "presentation",
  fire_size: "presentation",
  blur_region_width: "presentation",
  blur_region_height: "presentation",
  avatar_height: "presentation",
  avatar_debrief_height: "presentation",
  is_showing_user_text: "presentation",
  is_showing_avatar_text: "presentation",
  // config
  time_code: "config",
  is_pause_scene_on_play: "config",
  be_scored: "config",
  is_hidden_at_start: "config",
  timer: "config",
  catch_time: "config",
  game_duration: "config",
  nb_questions: "config",
  respond_timer: "config",
  time_begin: "config",
  page_to_start: "config",
  has_audio: "config",
  activate_auto_rec: "config",
  is_dialog_started_by_user: "config",
  is_silence_detection_activated: "config",
  min_silent_time_sec_before_avatar_reaction: "config",
  max_silent_time_sec_before_avatar_reaction: "config",
  is_speech_interruption_activated: "config",
  is_speech_interruption_by_user_activated: "config",
};

/** Unknown keys default to "config" — but the catalog test enforces that every
 * advanced spec is EXPLICITLY classified, so the default only covers drift. */
export const advancedFieldGroup = (fieldKey: string): "presentation" | "config" =>
  ADVANCED_FIELD_GROUPS[fieldKey] ?? "config";

/** Model names carrying a PER_TYPE entry — exported for the classification
 * coverage test (every advanced scalar must be explicitly grouped). */
export const CATALOGED_MODEL_NAMES: readonly string[] = Object.keys(PER_TYPE);
